import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Typography,
} from '@mui/material';
import { IconX } from '@tabler/icons-react';

const DeleteCohortDialog = ({ open, onClose, onConfirm, cohortName }) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleConfirm = async () => {
    setIsSubmitting(true);
    try {
      await onConfirm?.();
    } catch (error) {
      console.error('Error deleting cohort: ', error);
    } finally {
      setIsSubmitting(false);
      onClose?.();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? null : onClose}
      aria-labelledby="delete-cohort-dialog-title"
      sx={{ zIndex: (theme) => theme.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          borderRadius: '24px',
          padding: { xs: '24px 16px', sm: '32px' },
          width: '564px',
          maxWidth: '100%',
          overflowX: 'hidden',
          boxShadow: '0 22px 22px 0 rgba(0, 0, 0, 0.12), 0 7px 7px 0 rgba(0, 0, 0, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: 3,
          margin: { xs: 2, sm: 4 },
        },
      }}
    >
      <DialogTitle
        id="delete-cohort-dialog-title"
        sx={{ p: 0, mb: 0, display: 'flex', gap: 2, justifyContent: 'space-between', alignItems: 'flex-start' }}
      >
        <Typography
          sx={{
            p: 0,
            flex: 1,
            minWidth: 0,
            overflowWrap: 'anywhere',
            wordBreak: 'break-word',
            fontWeight: 600,
            fontStyle: 'normal',
            fontFamily: 'Rubik',
            fontSize: '20px',
            lineHeight: '28px',
            color: '#000',
          }}
        >
          Delete "{cohortName}"
        </Typography>
        {!isSubmitting && (
          <IconButton
            onClick={onClose}
            sx={{ p: 0, mt: '2px', flexShrink: 0, color: '#001A47', display: { xs: 'none', sm: 'inline-flex' } }}
            aria-label="close dialog"
          >
            <IconX size={24} />
          </IconButton>
        )}
      </DialogTitle>

      <DialogContent sx={{ p: 0, mb: 1 }}>
        <Typography
          sx={{
            color: '#000000',
            fontSize: '16px',
            lineHeight: '24px',
            fontFamily: 'Noto Sans',
            fontWeight: 400,
            fontStyle: 'normal',
          }}
        >
          This cohort will be permanently deleted for you and your team.
          <br />
          This action can't be undone.
        </Typography>
      </DialogContent>

      <DialogActions
        disableSpacing
        sx={{ p: 0, gap: 2, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}
      >
        <Button
          onClick={onClose}
          disabled={isSubmitting}
          variant="outlined"
          sx={{
            borderRadius: '24px',
            height: '44px',
            width: { xs: 'auto', sm: '84px' },
            flex: { xs: 'auto', sm: 'none' },
            textTransform: 'none',
            padding: { xs: '8px 12px', sm: '0 16px' },
            fontWeight: 600,
            borderColor: '#2F54A5',
            color: '#2F54A5',
            fontSize: { xs: '14px', sm: '16px' },
            lineHeight: '24px',
            fontFamily: 'Noto Sans',
            fontStyle: 'normal',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={isSubmitting}
          disableElevation
          sx={{
            borderRadius: '24px',
            height: '44px',
            width: 'auto',
            minWidth: { xs: 'auto', sm: '84px' },
            flex: { xs: 'auto', sm: 'none' },
            textTransform: 'none',
            padding: { xs: '8px 12px', sm: '0 16px' },
            backgroundColor: '#2F54A5',
            '&:hover': { backgroundColor: '#344e91' },
            '&.Mui-disabled': {
              backgroundColor: '#E0E0E0',
              pointerEvents: 'none',
            },
          }}
        >
          <Typography
            sx={{
              textAlign: 'center',
              fontWeight: 600,
              textWrap: 'nowrap',
              color: isSubmitting ? '#6B7280' : '#ffffff',
              fontSize: { xs: '14px', sm: '16px' },
              lineHeight: '24px',
              fontFamily: 'Noto Sans',
              fontStyle: 'normal',
            }}
          >
              {isSubmitting ? 'Deleting' : 'Delete'}
          </Typography>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteCohortDialog;
